import React, { useEffect, useRef, useState } from "react";
import {
  ArrowRight,
  ShieldCheck,
  Sparkles,
  Check,
  X,
  Zap,
  KeyRound,
  RefreshCw,
} from "lucide-react";
import { setOwnerMode, isOwnerMode, activateFoundingByEmail } from "../../lib/subscription";
import { useVehicle } from "../../lib/vehicleContext";

const API = process.env.REACT_APP_BACKEND_URL || "";
const TESLA_SESSION_KEY = "eviq.tesla_session_id";
const TESLA_USER_KEY = "eviq.tesla_user_name";
const SYNC_SECONDS = 30; // simulated first-sync duration; real telemetry hooks land in Step 2

/**
 * TeslaSignInCard
 * -------------------------------------------------------------
 * A single stateful component that owns the entire top-of-app
 * Tesla auth surface. Three states:
 *
 *   1. NOT CONNECTED (no session)
 *      → Big cyan "Sign in with Tesla" card with security bullets.
 *
 *   2. JUST CONNECTED (redirect just landed with ?tesla=connected&session_id)
 *      → "Welcome, <name>!" + animated sync-progress card counting down
 *         ~30 s while battery telemetry "starts syncing".
 *
 *   3. CONNECTED (session in localStorage, no fresh redirect)
 *      → Compact "Welcome, <name>" banner only (sign-in card is hidden).
 *         Includes a subtle Disconnect action.
 */
export const TeslaSignInCard = () => {
  const [sessionId, setSessionId] = useState(null);
  const [userName, setUserName] = useState("");
  const [isOwner, setIsOwner] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [fordNotice, setFordNotice] = useState(null);
  const [fordReason, setFordReason] = useState(null);
  const [fordBrand, setFordBrand] = useState("Ford");

  const fordBanner = fordNotice ? (
    <div
      data-testid="ford-connect-notice"
      className="rounded-xl border p-3 mb-2 text-[12px] leading-relaxed"
      style={fordNotice === "connected"
        ? { borderColor: "rgba(125,179,255,0.4)", background: "rgba(125,179,255,0.08)", color: "#fff" }
        : { borderColor: "rgba(255,92,92,0.4)", background: "rgba(255,92,92,0.07)", color: "#fff" }}
    >
      {fordNotice === "connected" ? (
        <>
          <span className="font-mono text-[10px] uppercase tracking-wider block mb-0.5" style={{ color: "#7DB3FF" }}>
            {fordBrand} connected
          </span>
          Your {fordBrand} vehicle is now linked. {fordBrand} battery insights are in early access —
          live cards are rolling out soon.
        </>
      ) : (
        <>
          <span className="font-mono text-[10px] uppercase tracking-wider block mb-0.5" style={{ color: "#FF5C5C" }}>
            {fordBrand} connection failed
          </span>
          Something went wrong during {fordBrand} sign-in — tap "Sign in with {fordBrand}" to try again.
          {fordReason && (
            <span className="block mt-1 font-mono text-[10px] text-zinc-400" data-testid="ford-error-reason">
              {fordBrand} says: {fordReason}
            </span>
          )}
        </>
      )}
    </div>
  ) : null;
  const [justConnected, setJustConnected] = useState(false);

  useEffect(() => {
    let cancelled = false;

    // Best-effort: use last-known name from localStorage on mount (may have
    // been set by the owner-unlock URL trigger in MobileApp.jsx even before
    // a Tesla session exists).
    try {
      const cached = localStorage.getItem(TESLA_USER_KEY);
      if (cached) setUserName(cached);
    } catch (_e) {}

    // Capture ?tesla=connected&session_id=… on redirect back from OAuth
    const params = new URLSearchParams(window.location.search);
    // Ford/Volvo OAuth redirects land with ?ford= / ?volvo= connected|error
    const fordParam = params.get("ford");
    const volvoParam = params.get("volvo");
    if (fordParam === "connected" || fordParam === "error") {
      setFordNotice(fordParam);
      if (params.get("reason")) setFordReason(params.get("reason"));
      window.history.replaceState({}, "", window.location.pathname);
    } else if (volvoParam === "connected" || volvoParam === "error") {
      setFordNotice(volvoParam);
      setFordBrand("Volvo");
      if (params.get("reason")) setFordReason(params.get("reason"));
      window.history.replaceState({}, "", window.location.pathname);
    }
    if (params.get("tesla") === "connected" && params.get("session_id")) {
      const sid = params.get("session_id");
      try {
        localStorage.setItem(TESLA_SESSION_KEY, sid);
      } catch (_e) {}
      // Silent auto-refresh once — Safari/PWA occasionally drops the
      // in-memory state after Tesla's cross-origin redirect, and the
      // `eviq:tesla-session-changed` event can fire before dependent
      // effects have registered their listeners. Guarded by a
      // sessionStorage flag so we never loop.
      try {
        const key = `eviq._oauth_reloaded:${sid}`;
        if (!sessionStorage.getItem(key)) {
          sessionStorage.setItem(key, "1");
          // Strip the OAuth params before reloading so the reloaded page
          // sees a clean URL and the effect only runs the "rehydrate"
          // branch below.
          window.history.replaceState({}, "", window.location.pathname);
          window.location.reload();
          return () => {
            cancelled = true;
          };
        }
      } catch (_e) {}
      window.dispatchEvent(
        new CustomEvent("eviq:tesla-session-changed", { detail: { sessionId: sid } }),
      );
      setSessionId(sid);
      setJustConnected(true);
      const clean = window.location.pathname;
      window.history.replaceState({}, "", clean);
      fetchStatus(sid, cancelled);
      return () => {
        cancelled = true;
      };
    }

    // Rehydrate existing session, if any
    let sid = null;
    try {
      sid = localStorage.getItem(TESLA_SESSION_KEY);
    } catch (_e) {}
    if (sid) {
      setSessionId(sid);
      // Best-effort: use last-known name from localStorage while we refresh.
      // NEVER overwrite with an empty string — that would blank the
      // owner_unlock-primed name before /api/tesla/status responds.
      try {
        const cachedName = localStorage.getItem(TESLA_USER_KEY);
        if (cachedName) setUserName(cachedName);
      } catch (_e) {}
      fetchStatus(sid, cancelled);
    }

    setDismissed(sessionStorage.getItem("eviq.tesla_card_dismissed") === "1");

    return () => {
      cancelled = true;
    };
  }, []);

  const fetchStatus = async (sid, cancelled) => {
    try {
      const r = await fetch(`${API}/api/tesla/status?session_id=${encodeURIComponent(sid)}`);
      if (!r.ok) return;
      const data = await r.json();
      if (cancelled || !data.connected) return;
      // Only overwrite the cached name if the backend actually returned one.
      // An empty response must not blank the owner_unlock-primed name.
      const name = data.user_name || "";
      if (name) {
        setUserName(name);
        try {
          localStorage.setItem(TESLA_USER_KEY, name);
        } catch (_e) {}
      }
      // Auto-flip owner mode when Tesla email matched OWNER_EMAILS on server.
      if (data.is_owner) {
        setIsOwner(true);
        setOwnerMode(true);
      }
      // Founding members: auto-activate lifetime access from the Tesla email.
      if (data.user_email) activateFoundingByEmail(data.user_email);
    } catch (_e) {
      // Silent — the connected banner still renders with a friendly default.
    }
  };

  const disconnect = async () => {
    try {
      await fetch(`${API}/api/tesla/disconnect`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId }),
      });
    } catch (_e) {}
    try {
      localStorage.removeItem(TESLA_SESSION_KEY);
      localStorage.removeItem(TESLA_USER_KEY);
    } catch (_e) {}
    setSessionId(null);
    setUserName("");
    setJustConnected(false);
  };

  // --- STATE 2: just connected — show welcome + sync progress -------------
  if (justConnected && sessionId) {
    return (
      <SyncProgressCard
        name={userName}
        onFinish={() => setJustConnected(false)}
      />
    );
  }

  // --- STATE 3: connected — compact welcome banner only -------------------
  if (sessionId) {
    return (
      <>
        {fordBanner}
        <WelcomeChip
          name={userName}
          isOwner={isOwner}
          hasTeslaSession={true}
          onDisconnect={disconnect}
        />
      </>
    );
  }

  // --- STATE 1: not connected — full sign-in card -------------------------
  // Note: even if owner-mode is enabled, we always show the full sign-in
  // card until a real Tesla session exists. Otherwise the user has no way
  // to reach the OAuth flow (previously the WelcomeChip was rendered in
  // this state, which had no sign-in button — a P0 dead-end bug).
  if (dismissed) return fordBanner;

  const dismiss = () => {
    sessionStorage.setItem("eviq.tesla_card_dismissed", "1");
    setDismissed(true);
  };

  return (
    <div
      data-testid="tesla-signin-card"
      className="relative rounded-2xl border border-[#00E5FF]/30 bg-gradient-to-br from-[#00E5FF]/[0.10] via-black to-[#39FF14]/[0.06] p-4 mb-2"
      style={{ boxShadow: "0 10px 40px -20px rgba(0,229,255,0.5)" }}
    >
      {fordBanner}
      <button
        type="button"
        onClick={dismiss}
        aria-label="Dismiss Tesla sign-in card"
        data-testid="tesla-signin-dismiss"
        className="absolute top-2.5 right-2.5 p-1.5 rounded-full text-zinc-500 hover:text-white hover:bg-white/5 transition-colors"
      >
        <X className="w-3.5 h-3.5" strokeWidth={2} aria-hidden="true" />
      </button>

      <div className="flex items-start gap-3 mb-3 pr-6">
        <div
          className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
          style={{ background: "rgba(0,229,255,0.12)", border: "1px solid rgba(0,229,255,0.4)" }}
          aria-hidden="true"
        >
          <Sparkles className="w-5 h-5" style={{ color: "#00E5FF" }} strokeWidth={2.2} />
        </div>
        <div className="min-w-0">
          <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-zinc-500 mb-0.5">
            Connect your vehicle
          </div>
          <div className="text-sm font-semibold text-white leading-tight">
            Unlock live battery intelligence
          </div>
          <div className="text-[11px] text-zinc-400 mt-1 leading-relaxed">
            Read-only Fleet API access. Cancel anytime.
          </div>
        </div>
      </div>

      <div className="flex items-center gap-2">
        <a
          href="/connect"
          data-testid="tesla-signin-cta"
          className="brand-gradient text-black font-semibold rounded-full px-4 py-2.5 inline-flex items-center justify-center gap-2 flex-1 glow-green hover:opacity-90 transition-opacity text-sm"
        >
          Sign in
          <ArrowRight className="w-4 h-4" strokeWidth={2.5} aria-hidden="true" />
        </a>
        {/* Manual refresh — for when the OAuth redirect was interrupted
            (Safari sometimes drops the query string) or after granting
            EVIQ access in the Tesla mobile app. A hard reload picks up
            the fresh session and re-runs the vehicles + wake-up flow. */}
        <button
          type="button"
          onClick={() => window.location.reload()}
          data-testid="tesla-signin-refresh"
          aria-label="Refresh to retry Tesla connection"
          title="Refresh page to retry Tesla connection"
          className="shrink-0 w-10 h-10 rounded-full border border-white/15 bg-white/[0.03] hover:bg-white/[0.08] transition-colors flex items-center justify-center"
        >
          <RefreshCw className="w-4 h-4 text-zinc-300" strokeWidth={2.2} aria-hidden="true" />
        </button>
      </div>

      <div className="mt-3 grid grid-cols-3 gap-1.5 text-[10px] font-mono uppercase tracking-[0.1em] text-zinc-500">
        <span className="inline-flex items-center gap-1">
          <ShieldCheck className="w-3 h-3 text-zinc-400" strokeWidth={2} aria-hidden="true" />
          Read-only
        </span>
        <span className="inline-flex items-center gap-1 justify-center">
          <ShieldCheck className="w-3 h-3 text-zinc-400" strokeWidth={2} aria-hidden="true" />
          AES-256
        </span>
        <span className="inline-flex items-center gap-1 justify-end">
          <ShieldCheck className="w-3 h-3 text-zinc-400" strokeWidth={2} aria-hidden="true" />
          Revocable
        </span>
      </div>
    </div>
  );
};

/** State 2: right after OAuth — welcome + 30-second sync countdown. */
const SyncProgressCard = ({ name, onFinish }) => {
  const [elapsed, setElapsed] = useState(0);
  const startedAt = useRef(Date.now());

  useEffect(() => {
    const id = setInterval(() => {
      const secs = (Date.now() - startedAt.current) / 1000;
      if (secs >= SYNC_SECONDS) {
        clearInterval(id);
        setElapsed(SYNC_SECONDS);
        // Show "complete" state briefly, then collapse to the WelcomeChip
        setTimeout(onFinish, 1800);
        return;
      }
      setElapsed(+secs.toFixed(1));
    }, 200);
    return () => clearInterval(id);
  }, [onFinish]);

  const pct = Math.min(100, (elapsed / SYNC_SECONDS) * 100);
  const remaining = Math.max(0, Math.ceil(SYNC_SECONDS - elapsed));
  const done = pct >= 100;
  const first = (name || "").split(" ")[0] || "there";

  return (
    <div
      data-testid="tesla-sync-progress"
      className="relative rounded-2xl border border-[#39FF14]/35 bg-gradient-to-br from-[#39FF14]/[0.10] via-black to-[#00E5FF]/[0.06] p-4 mb-2"
      style={{ boxShadow: "0 10px 40px -20px rgba(57,255,20,0.55)" }}
    >
      <div className="flex items-start gap-3 mb-3">
        <div
          className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
          style={{ background: "rgba(57,255,20,0.12)", border: "1px solid rgba(57,255,20,0.45)" }}
          aria-hidden="true"
        >
          {done ? (
            <Check className="w-5 h-5" style={{ color: "#39FF14" }} strokeWidth={2.6} />
          ) : (
            <Zap className="w-5 h-5" style={{ color: "#39FF14" }} strokeWidth={2.2} />
          )}
        </div>
        <div className="min-w-0 flex-1">
          <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-zinc-500 mb-0.5">
            {done ? "Sync complete" : "Tesla account connected"}
          </div>
          <div
            className="text-sm font-semibold text-white leading-tight"
            data-testid="tesla-sync-welcome"
          >
            {done ? `You're all set, ${first}` : `Welcome, ${first}`}
          </div>
          <div className="text-[11px] text-zinc-400 mt-1 leading-relaxed">
            {done
              ? "Live battery telemetry is available across the app."
              : "Your battery telemetry will start syncing shortly."}
          </div>
        </div>
      </div>

      {/* Progress bar */}
      <div
        className="h-2 rounded-full bg-white/[0.06] overflow-hidden"
        data-testid="tesla-sync-bar"
        role="progressbar"
        aria-valuenow={Math.round(pct)}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label="Tesla battery telemetry sync progress"
      >
        <div
          className="h-full transition-[width] duration-200 ease-linear"
          style={{
            width: `${pct}%`,
            background: "linear-gradient(90deg, #00E5FF 0%, #39FF14 100%)",
            boxShadow: "0 0 10px rgba(57,255,20,0.6)",
          }}
        />
      </div>

      <div className="mt-2 flex items-center justify-between text-[10px] font-mono uppercase tracking-[0.14em] text-zinc-500">
        <span data-testid="tesla-sync-pct">{Math.round(pct)}%</span>
        <span data-testid="tesla-sync-countdown">
          {done ? "Complete" : `${remaining}s remaining`}
        </span>
      </div>
    </div>
  );
};

/** State 3: quiet welcome banner shown on every subsequent load. */
const WelcomeChip = ({ name, isOwner, hasTeslaSession = false, onDisconnect }) => {
  const { vehicle } = useVehicle();
  // Greet the PERSON (Tesla account name). Only fall back to the vehicle's
  // custom name — and never the generic "Tesla" default, which made the
  // banner flip from the real name to "Welcome, Tesla" once the vehicle
  // profile merged.
  const firstName = (name || "").trim().split(/\s+/)[0];
  const carName =
    hasTeslaSession && vehicle?.displayName && vehicle.displayName.toLowerCase() !== "tesla"
      ? vehicle.displayName
      : null;
  const displayLabel = firstName
    ? `Welcome, ${firstName}`
    : carName
    ? `Welcome, ${carName}`
    : "Welcome back";

  // Show the last 6 chars of the VIN as a subtle chip — only when we
  // have a real Tesla session AND the vehicle profile has merged. Never
  // display the mock catalog VIN so we don't mislead the user into
  // thinking they're seeing real Tesla data.
  const vinTail =
    hasTeslaSession &&
    vehicle?.live &&
    vehicle?.vin &&
    typeof vehicle.vin === "string" &&
    vehicle.vin.length >= 6
      ? vehicle.vin.slice(-6).toUpperCase()
      : null;

  // Sub-line: honest about what state we're in.
  const brandLabel = vehicle?.brand === "ford" ? "Ford" : vehicle?.brand === "volvo" ? "Volvo" : "Tesla";
  const subLine = hasTeslaSession
    ? `${brandLabel} · live telemetry`
    : `Owner mode · sign in with ${brandLabel} for live data`;

  return (
    <div
      data-testid="tesla-welcome-banner"
      className="flex items-center justify-between gap-2 rounded-xl border border-[#39FF14]/25 bg-[#39FF14]/[0.05] px-3 py-2 mb-2"
    >
      <div className="flex items-center gap-2 min-w-0">
        <div
          className="w-7 h-7 rounded-lg flex items-center justify-center shrink-0"
          style={{ background: "rgba(57,255,20,0.12)", border: "1px solid rgba(57,255,20,0.45)" }}
          aria-hidden="true"
        >
          <Check className="w-4 h-4" style={{ color: "#39FF14" }} strokeWidth={2.6} />
        </div>
        <div className="min-w-0">
          <div className="text-[13px] font-semibold text-white leading-tight truncate flex items-center gap-1.5">
            <span className="truncate">{displayLabel}</span>
            {isOwner && (
              <span
                data-testid="tesla-owner-badge"
                className="shrink-0 inline-flex items-center gap-1 text-[9px] font-mono uppercase tracking-[0.14em] text-[#FFB020] border border-[#FFB020]/45 bg-[#FFB020]/[0.10] rounded-full px-1.5 py-0.5"
              >
                <KeyRound className="w-2.5 h-2.5" strokeWidth={2.4} aria-hidden="true" />
                Owner
              </span>
            )}
          </div>
          <div className="font-mono text-[9px] uppercase tracking-[0.16em] text-zinc-500 truncate flex items-center gap-1.5">
            <span>{subLine}</span>
            {vinTail && (
              <>
                <span className="text-zinc-700">·</span>
                <span
                  data-testid="tesla-vin-chip"
                  className="text-zinc-400 tracking-[0.18em]"
                  title={`VIN: ${vehicle.vin}`}
                >
                  VIN {vinTail}
                </span>
              </>
            )}
          </div>
        </div>
      </div>
      {hasTeslaSession && (
        <button
          type="button"
          onClick={onDisconnect}
          data-testid="tesla-disconnect"
          className="text-[10px] font-mono uppercase tracking-[0.14em] text-zinc-400 hover:text-white transition-colors px-2 py-1 rounded-full border border-white/10 hover:border-white/25"
        >
          Disconnect
        </button>
      )}
    </div>
  );
};
